import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDaysIcon,
  ClockIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError, toastSuccess } from "../utils/toast";

const DAYS = [null, "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const TYPES = {
  infrasettimanale: "Adunanza infrasettimanale",
  fine_settimana: "Adunanza fine settimana",
};

const emptyForm = (tipo = "infrasettimanale") => ({
  tipo,
  giorno_settimana: "",
  ora_inizio: "",
  ora_fine: "",
  stato: "attiva",
});

const AdunanzeSettimanali = () => {
  const { user, activeCongregazione } = useAuth();
  const [adunanze, setAdunanze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const canManage = user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const isSpecial = Boolean(activeCongregazione?.servizio_speciale);

  const load = useCallback(async () => {
    if (!activeCongregazione?.id || isSpecial) {
      setAdunanze([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await api.get("/adunanze-settimanali");
      setAdunanze(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile caricare le adunanze settimanali");
    } finally {
      setLoading(false);
    }
  }, [activeCongregazione?.id, isSpecial]);

  useEffect(() => {
    load();
  }, [load]);

  const configuredTypes = useMemo(() => new Set(adunanze.map((item) => item.tipo)), [adunanze]);

  const openCreate = (tipo) => {
    setEditing(null);
    setForm(emptyForm(tipo));
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      tipo: item.tipo,
      giorno_settimana: String(item.giorno_settimana || ""),
      ora_inizio: String(item.ora_inizio || "").slice(0, 5),
      ora_fine: String(item.ora_fine || "").slice(0, 5),
      stato: item.stato || "attiva",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.giorno_settimana || !form.ora_inizio || !form.ora_fine) {
      toastError("Inserisci giorno, ora di inizio e ora di fine");
      return;
    }
    if (form.ora_fine <= form.ora_inizio) {
      toastError("L'ora di fine deve essere successiva all'ora di inizio");
      return;
    }
    const payload = { ...form, giorno_settimana: Number(form.giorno_settimana) };
    try {
      setSaving(true);
      if (editing) {
        await api.put(`/adunanze-settimanali/${editing.id}`, payload);
        toastSuccess("Adunanza settimanale aggiornata");
      } else {
        await api.post("/adunanze-settimanali", payload);
        toastSuccess("Adunanza settimanale configurata");
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile salvare l'adunanza settimanale");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare “${TYPES[item.tipo]}”? Le pulizie già programmate non verranno cancellate.`)) return;
    try {
      await api.delete(`/adunanze-settimanali/${item.id}`);
      toastSuccess("Adunanza settimanale eliminata");
      await load();
    } catch (error) {
      toastError(error.response?.data?.message || "Impossibile eliminare l'adunanza");
    }
  };

  if (isSpecial) return null;

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 md:p-6">
      <header className="rounded-xl bg-white p-5 shadow-sm md:p-7">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700">
            <CalendarDaysIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Adunanze settimanali</h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 md:text-base">
              Imposta il giorno e l'orario abituali delle adunanze. Questi orari permettono di inserire correttamente le pulizie nel calendario dei proclamatori.
            </p>
            <div className="accent-line mt-3" />
          </div>
        </div>
      </header>

      <section className="rounded-xl bg-white p-4 shadow-sm md:p-6">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Configurazione settimanale</h2>
            <p className="text-sm text-gray-500">Una configurazione per ciascun tipo di adunanza.</p>
          </div>
          {canManage && <span className="text-sm font-medium text-primary-700">{adunanze.length}/2 configurate</span>}
        </div>

        {loading ? (
          <p className="py-10 text-center text-gray-600">Caricamento in corso…</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {Object.entries(TYPES).map(([tipo, label]) => {
              const item = adunanze.find((adunanza) => adunanza.tipo === tipo);
              return (
                <article key={tipo} className="rounded-xl border border-gray-200 p-4 md:p-5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">{label}</p>
                  {item ? (
                    <>
                      <div className="mt-4 flex items-center gap-3">
                        <ClockIcon className="h-5 w-5 shrink-0 text-gray-400" />
                        <div>
                          <p className="font-semibold text-gray-900">{DAYS[item.giorno_settimana]}</p>
                          <p className="text-sm text-gray-600">{String(item.ora_inizio).slice(0, 5)} – {String(item.ora_fine).slice(0, 5)}</p>
                        </div>
                      </div>
                      {item.stato === "inattiva" && <p className="mt-3 text-sm font-medium text-amber-700">Configurazione disattivata</p>}
                      {canManage && (
                        <div className="mt-5 flex gap-2">
                          <button type="button" onClick={() => openEdit(item)} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-primary-300 px-3 text-sm font-semibold text-primary-700 hover:bg-primary-50">
                            <PencilIcon className="h-4 w-4" /> Modifica
                          </button>
                          <button type="button" onClick={() => remove(item)} className="inline-flex min-h-11 w-11 items-center justify-center rounded-lg text-red-600 hover:bg-red-50" aria-label={`Elimina ${label}`}>
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="mt-4">
                      <p className="text-sm leading-6 text-gray-500">Non ancora configurata.</p>
                      {canManage && <button type="button" onClick={() => openCreate(tipo)} className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700">
                        <PlusIcon className="h-5 w-5" /> Configura
                      </button>}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {modalOpen && canManage && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-4">
          <form onSubmit={save} className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-lg sm:rounded-2xl md:p-6">
            <h2 className="text-xl font-bold text-gray-900">{editing ? "Modifica" : "Configura"} {TYPES[form.tipo]}</h2>
            <p className="mt-1 text-sm text-gray-600">L'orario verrà usato per le pulizie associate a questa adunanza.</p>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-semibold text-gray-700">Tipologia
                <select value={form.tipo} disabled={Boolean(editing)} onChange={(e) => setForm((current) => ({ ...current, tipo: e.target.value }))} className="mt-1 min-h-11 w-full rounded-lg border border-gray-300 px-3 disabled:bg-gray-100">
                  {Object.entries(TYPES).map(([value, label]) => <option key={value} value={value} disabled={!editing && configuredTypes.has(value)}>{label}</option>)}
                </select>
              </label>
              <label className="block text-sm font-semibold text-gray-700">Giorno
                <select value={form.giorno_settimana} onChange={(e) => setForm((current) => ({ ...current, giorno_settimana: e.target.value }))} className="mt-1 min-h-11 w-full rounded-lg border border-gray-300 px-3" required>
                  <option value="">Seleziona il giorno</option>
                  {DAYS.slice(1).map((day, index) => <option key={day} value={index + 1}>{day}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block text-sm font-semibold text-gray-700">Ora di inizio
                  <input type="time" value={form.ora_inizio} onChange={(e) => setForm((current) => ({ ...current, ora_inizio: e.target.value }))} className="mt-1 min-h-11 w-full rounded-lg border border-gray-300 px-3" required />
                </label>
                <label className="block text-sm font-semibold text-gray-700">Ora di fine
                  <input type="time" value={form.ora_fine} onChange={(e) => setForm((current) => ({ ...current, ora_fine: e.target.value }))} className="mt-1 min-h-11 w-full rounded-lg border border-gray-300 px-3" required />
                </label>
              </div>
              {editing && <label className="flex min-h-11 items-center gap-3 text-sm font-medium text-gray-700"><input type="checkbox" checked={form.stato === "attiva"} onChange={(e) => setForm((current) => ({ ...current, stato: e.target.checked ? "attiva" : "inattiva" }))} className="h-5 w-5 rounded border-gray-300 text-primary-600" />Configurazione attiva</label>}
            </div>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={closeModal} disabled={saving} className="min-h-11 rounded-lg px-4 text-sm font-semibold text-gray-700 hover:bg-gray-100">Annulla</button>
              <button type="submit" disabled={saving} className="min-h-11 rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">{saving ? "Salvataggio…" : "Salva configurazione"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdunanzeSettimanali;
